import { CreatorSiphon } from '@/types/creator';
import { Siphon } from './objects/Siphon';
import { ProjectRenderer } from './ProjectRenderer';
import { UUID } from '@/types/types';
import { Object3D } from 'three';
import { ThreeEvent } from './types';
import { clamp } from 'lodash';

export class SiphonsManager {
	siphonTemplate: Object3D | null = null;
	private _isAutoSiphonsPositioning = true;
	private siphonsMap = new Map<UUID, Siphon>();
	siphonWidth = 0;

	static readonly SIPHONS_MIN_GAP = 0.005;
	static readonly SIPHONS_MIN_DIST_TO_END = 0.14;

	constructor(private renderer: ProjectRenderer) {}

	get isAutoSiphonsPositioning() {
		return this._isAutoSiphonsPositioning;
	}

	set isAutoSiphonsPositioning(value: boolean) {
		this._isAutoSiphonsPositioning = value;
		this.autoRepositionSiphons();
	}

	get siphonsXRange() {
		const { distance } =
			this.renderer.productBuilder.getAvailableSiphonsAxisData();

		return {
			min: SiphonsManager.SIPHONS_MIN_DIST_TO_END,
			max: distance - SiphonsManager.SIPHONS_MIN_DIST_TO_END,
		};
	}

	checkCollision() {
		const siphons = Array.from(this.siphonsMap.values());

		for (const siphon of siphons)
			siphon.isColiding = siphons.some(
				(other) => other !== siphon && siphon.box.intersectsBox(other.box)
			);
	}

	syncWithCreatorState(creatorSiphons: CreatorSiphon[]) {
		if (!this.siphonTemplate) return;

		this.siphonsMap.forEach((siphon) => {
			const creatorSiphon = creatorSiphons.find((s) => s.id === siphon.id);

			if (creatorSiphon) return;

			siphon.dispose();
			this.siphonsMap.delete(siphon.id);
		});

		for (const creatorSiphon of creatorSiphons) {
			const existingSiphon = this.siphonsMap.get(creatorSiphon.id);
			if (existingSiphon) continue;

			const siphon = new Siphon(
				this.renderer,
				creatorSiphon.id,
				this.siphonTemplate!.clone()
			);

			this.siphonsMap.set(creatorSiphon.id, siphon);
		}

		this.autoRepositionSiphons();
		this.renderer.productMeasures.draw();
	}

	setSiphonPosX(id: UUID, posX: number) {
		const siphon = this.siphonsMap.get(id);
		if (!siphon) return;

		siphon.posX = posX;
		this.checkCollision();
		this.renderer.productMeasures.draw();
	}

	autoRepositionSiphons() {
		if (!this.siphonTemplate) return;
		if (!this.isAutoSiphonsPositioning) return;

		const { distance } =
			this.renderer.productBuilder.getAvailableSiphonsAxisData();

		const segmentWidth = distance / this.siphonsMap.size;

		Array.from(this.siphonsMap.values()).forEach((siphon, index) => {
			siphon.posX = (index + 1) * segmentWidth - segmentWidth / 2;

			this.renderer.emitter.emit(ThreeEvent.SET_SIPHON_POS_X, {
				id: siphon.id,
				posX: Math.round(siphon.posX * 1000),
			});
		});

		this.renderer.productMeasures.draw();
	}

	fitSiphonsToGutter() {
		if (this.isAutoSiphonsPositioning) {
			this.autoRepositionSiphons();
			return;
		}

		const { min, max } = this.siphonsXRange;

		this.siphonsMap.forEach((siphon) => {
			siphon.posX = clamp(siphon.posX, min, max);

			this.renderer.emitter.emit(ThreeEvent.SET_SIPHON_POS_X, {
				id: siphon.id,
				posX: Math.round(siphon.posX * 1000),
			});
		});

		this.checkCollision();
		this.renderer.productMeasures.draw();
	}

	getSiphonsArray() {
		return Array.from(this.siphonsMap.values()).sort((a, b) => {
			return a.posX - b.posX;
		});
	}

	clear() {
		this.siphonsMap.forEach((siphon) => {
			siphon.dispose();
		});

		this.siphonsMap.clear();
	}
}
